import { IZip, IFetcher, IFetchRequest, Method, IFetchResponse } from '../../interface/util';
import { IProgressPromise, IProgressPromiseGenerator } from '../../interface/promise';
import { ISiteData } from '../interface/siteData';

const _isRunningOnNodeJS = () => typeof window === 'undefined';

function _isBlob(object: unknown): object is Blob {
    return object instanceof Blob;
}

function _isArrayBuffer(object: unknown): object is ArrayBuffer {
    return object instanceof ArrayBuffer;
}

function _arrayBufferToString(buffer: ArrayBuffer): Promise<string> {
    const arr = new Uint16Array(buffer);
    const result = String.fromCharCode.apply(null, Array.from(arr));
    return Promise.resolve(result);
}

function _blobToString(blob: Blob): Promise<string> {
    return blob.text();
}

function _request(
    apiUrl: string,
    apiExtension: string,
    authToken: string,
    hash: string,
): IFetchRequest {
    const req: IFetchRequest = {
        url: apiUrl + apiExtension,
        method: 'POST' as Method,
        headers: [
            { name: 'Authorization', value: `Bearer ${authToken}` },
            { name: 'Content-Type', value: 'application/json' },
        ],
        body: JSON.stringify({ hash: hash }),
        expectedResponseType: _isRunningOnNodeJS() ? 'arraybuffer' : 'blob',
    };

    return req;
}

function _downloadSiteData(
    fetcher: IFetcher,
    promiser: IProgressPromiseGenerator,
    apiUrl: string,
    apiExtension: string,
    authToken: string,
    hash: string = '',
    timeout = 20 * 10000,
): IProgressPromise<Blob | ArrayBuffer> {
    return promiser.promise(function (resolve, reject, progress) {
        const req = _request(apiUrl, apiExtension, authToken, hash);
        const promise = fetcher<Blob>(req, timeout);
        promise.onProgress(function (data) {
            progress(data);
        });

        promise
            .then(function (response: IFetchResponse<Blob>) {
                if (!response.payload) {
                    reject('Missing payload from response');
                }

                const blob = new Blob([response.payload as Blob]);

                resolve(blob);
            })
            .catch(function (err) {
                reject(err);
            });
    });
}

const PATCH_DATA_FILE_NAME = 'data.json';

function _unzipSiteData(
    promiser: IProgressPromiseGenerator,
    zip: IZip,
    srcData: Blob | ArrayBuffer,
): IProgressPromise<ISiteData> {
    return promiser.promise(function (resolve, reject, progress) {
        zip.unzipFile(srcData, PATCH_DATA_FILE_NAME)
            .then(function (dataFile) {
                progress(0.9);

                if (_isBlob(dataFile)) {
                    return _blobToString(dataFile);
                } else if (_isArrayBuffer(dataFile)) {
                    return _arrayBufferToString(dataFile);
                } else {
                    reject(new Error(`unknown zip-file response type: ${dataFile}`));
                    return '';
                }
            })
            .then(dataStr => {
                const jsonData = JSON.parse(dataStr) as ISiteData;
                resolve(jsonData);
            })
            .catch(function (err) {
                reject(err);
            });
    });
}

function getSiteData(
    promiser: IProgressPromiseGenerator,
    fetcher: Promise<IFetcher>,
    zipper: Promise<IZip>,
    apiUrl: string,
    apiExtension: string,
    authToken: string,
    hash: string = '',
    timeout = 20 * 10000,
): IProgressPromise<ISiteData> {
    return promiser.promise(async function (resolve, reject, progress) {
        const fetchGet = await fetcher;
        const zip = await zipper;

        const promise = _downloadSiteData(
            fetchGet,
            promiser,
            apiUrl,
            apiExtension,
            authToken,
            hash,
            timeout,
        );

        promise.onProgress(function (data) {
            progress(data / 2); /* 2 Jobs */
        });

        promise
            .then(srcData => _unzipSiteData(promiser, zip, srcData))
            .then(sitedata => resolve(sitedata))
            .catch(err => reject(err));
    });
}

export default getSiteData;
